import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';

const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: 'Inter', 'Segoe UI', sans-serif;
    background: #eef2f7;
    color: #0f172a;
  }
`;

const Page = styled.div`
  min-height: 100vh;
  padding: 24px 20px;
`;

const Shell = styled.div`
  max-width: 980px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
`;

const Content = styled.main`
  padding: 22px 24px;
`;

const EmptyState = styled.div`
  padding: 12px 0;
  color: #64748b;
  font-size: 14px;
`;

const TranscriptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TranscriptLine = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: #0f172a;
  white-space: pre-wrap;
  word-break: break-word;
`;

const InlineLink = styled.a`
  color: #1d4ed8;
  text-decoration: underline;
`;

const Loading = styled.div`
  padding: 12px 0;
  color: #64748b;
  font-size: 14px;
`;

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isSystemMessage = (message) => String(message || '').startsWith('__SYSTEM__:');

const normalizeMessageText = (message) => {
  if (isSystemMessage(message)) {
    return String(message).replace('__SYSTEM__:', '');
  }
  return String(message || '');
};

const linkPattern = /(https?:\/\/[^\s]+)/g;

const renderLinkedText = (text) => {
  const value = String(text || '');
  if (!value) return value;

  return value.split(linkPattern).map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <InlineLink key={`${part}-${index}`} href={part} target="_blank" rel="noreferrer noopener">
          {part}
        </InlineLink>
      );
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const buildTranscriptLine = (message, index) => {
  const senderName = isSystemMessage(message.message) ? 'System' : (message._sender_name || 'Staff');
  const text = normalizeMessageText(message.message) || (message.attachment_link ? `Attachment: ${message.attachment_link}` : '');
  return (
    <>
      {`${index + 1} - `}
      {renderLinkedText(text)}
      {` by ${senderName} on (${formatDateTime(message.created_at)})`}
    </>
  );
};

export default function TeamworkChatDetail() {
  const location = useLocation();
  const teamworkId = useMemo(() => new URLSearchParams(location.search).get('teamworkId'), [location.search]);
  const stored = AuthService.getUser();
  const currentUser = stored?.superAdmin || {};
  const currentStaff = currentUser?.staff || {};
  const isSuperAdmin = Boolean(currentUser?.superAdminId);
  const normalizedRole = String(
    currentStaff?.role || currentUser?.role || currentStaff?.staffType || currentStaff?.type || ''
  ).trim().toLowerCase();
  const isAdminUser = isSuperAdmin || normalizedRole === 'admin' || normalizedRole === 'administrator';

  const [teamwork, setTeamwork] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadChatDetail = async () => {
      if (!isAdminUser) {
        setError('Only admin can view chat detail.');
        setLoading(false);
        return;
      }

      if (!teamworkId) {
        setError('No teamwork selected. Open this page from a teamwork thread.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const [teamworkRes, messageRes] = await Promise.all([
          axiosInstance.get('/admin/staff-teamwork'),
          axiosInstance.get(`/admin/staff-teamwork/${teamworkId}/messages`),
        ]);

        if (cancelled) return;

        const teamworkList = teamworkRes?.data?.teamworks || teamworkRes?.data || [];
        const currentTeamwork = Array.isArray(teamworkList)
          ? teamworkList.find((item) => String(item.teamworkId) === String(teamworkId))
          : null;

        const messageList = messageRes?.data?.messages || messageRes?.data || [];

        setTeamwork(currentTeamwork || null);
        setMessages(Array.isArray(messageList) ? messageList : []);

        if (!currentTeamwork) {
          setError('The selected teamwork could not be found.');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load chat detail.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadChatDetail();

    return () => {
      cancelled = true;
    };
  }, [isAdminUser, teamworkId]);

  return (
    <>
      <GlobalStyle />
      <Page>
        <Shell>
          <Content>
            {loading ? (
              <Loading>Loading chat detail...</Loading>
            ) : error && !teamwork ? (
              <EmptyState>{error}</EmptyState>
            ) : messages.length === 0 ? (
              <EmptyState>No chat messages found.</EmptyState>
            ) : (
              <TranscriptList>
                {messages.map((message, index) => (
                  <TranscriptLine key={message.messageId}>
                    {buildTranscriptLine(message, index)}
                  </TranscriptLine>
                ))}
              </TranscriptList>
            )}
          </Content>
        </Shell>
      </Page>
    </>
  );
}